/* Shared board membership, cart order and verified cart cleanup. Private data stays local. */
(() => {
  'use strict';
  if (window.HalfpipedBoardIntegrity) return;
  const bundleOf = item => String(item?.properties?._hp_board_bundle_id || '');
  const compare = (a, b) => a < b ? -1 : a > b ? 1 : 0;
  const sortedProperties = properties => Object.fromEntries(Object.entries(properties || {}).sort(([a], [b]) => compare(a, b)).map(([key, value]) => [key, String(value)]));
  const sellingPlan = item => String(item?.selling_plan_id || item?.selling_plan || item?.selling_plan_allocation?.selling_plan?.id || item?.selling_plan_allocation?.selling_plan_id || item?.sellingPlanId || '');
  const normalizedItem = item => ({ variantId: String(item.variantId || item.variant_id || item.id || ''), quantity: Number(item.quantity), properties: sortedProperties(item.properties), sellingPlanId: sellingPlan(item) || null });

  // Synchronous SHA-256 lets the persisted order apply before the first paint.
  // Hash the complete token (including Shopify's secret suffix), never its prefix.
  const sha256 = value => {
    const bytes = new TextEncoder().encode(String(value));
    const words = [];
    for (let i = 0; i < bytes.length; i++) words[i >> 2] = (words[i >> 2] || 0) | bytes[i] << (24 - (i % 4) * 8);
    words[bytes.length >> 2] = (words[bytes.length >> 2] || 0) | 0x80 << (24 - (bytes.length % 4) * 8);
    const size = ((bytes.length + 9 + 63) >> 6) * 16;
    words[size - 2] = Math.floor(bytes.length / 0x20000000);
    words[size - 1] = bytes.length * 8;
    const k = [0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2];
    const h = [0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19];
    const rotate = (x, n) => (x >>> n) | (x << (32 - n));
    for (let offset = 0; offset < size; offset += 16) {
      const w = Array.from({ length: 64 }, (_, i) => words[offset + i] || 0);
      for (let i = 16; i < 64; i++) {
        const x = w[i - 15], y = w[i - 2];
        w[i] = (w[i - 16] + (rotate(x, 7) ^ rotate(x, 18) ^ (x >>> 3)) + w[i - 7] + (rotate(y, 17) ^ rotate(y, 19) ^ (y >>> 10))) | 0;
      }
      let [a,b,c,d,e,f,g,hh] = h;
      for (let i = 0; i < 64; i++) {
        const t1 = (hh + (rotate(e, 6) ^ rotate(e, 11) ^ rotate(e, 25)) + ((e & f) ^ (~e & g)) + k[i] + w[i]) | 0;
        const t2 = ((rotate(a, 2) ^ rotate(a, 13) ^ rotate(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
        hh=g; g=f; f=e; e=(d+t1)|0; d=c; c=b; b=a; a=(t1+t2)|0;
      }
      [a,b,c,d,e,f,g,hh].forEach((value, i) => { h[i] = (h[i] + value) | 0; });
    }
    return h.map(value => (value >>> 0).toString(16).padStart(8, '0')).join('');
  };
  const fingerprint = async payload => sha256(JSON.stringify({
    cartToken: String(payload.cartToken || ''), currency: String(payload.currency || ''),
    items: (payload.items || []).map(normalizedItem).sort((a, b) => compare(JSON.stringify(a), JSON.stringify(b))),
    boardOrder: payload.boardOrder || [],
  }));
  const manifestOf = item => {
    try {
      const raw = item?.properties?._hp_board_manifest;
      if (typeof raw !== 'string' || raw.length > 1024) return null;
      const data = JSON.parse(raw);
      const components = new Set(['deck','grip','wheel','bearing','truck','hardware','skate_tool','assembly']);
      if (data.v !== 1 || !Number.isSafeInteger(data.created) || data.created <= 0 || !Array.isArray(data.parts) || !data.parts.length || data.parts.length > 8) return null;
      for (const part of data.parts) {
        if (!Array.isArray(part) || part.length !== 3 || !components.delete(part[0]) || typeof part[1] !== 'string' || !/^[1-9]\d*$/.test(part[1]) || part[2] !== 1) return null;
      }
      return data;
    } catch { return null; }
  };
  const lineIdentity = item => JSON.stringify({ variantId: String(item.variant_id || item.variantId || item.id || ''), properties: sortedProperties(item.properties), sellingPlanId: sellingPlan(item) || null });
  const unitOf = item => bundleOf(item) ? `board:${bundleOf(item)}` : `line:${lineIdentity(item)}`;
  const memory = new Map();
  const ledgerMemoryOnly = new Set();
  let activeScope = null;
  let latestCart = null;
  const scopeOf = cart => cart?.token ? `hp-cart-order:v1:${window.Shopify?.shop || window.location.hostname}:${sha256(String(cart.token))}` : null;
  const readLedger = key => {
    if (ledgerMemoryOnly.has(key)) return memory.get(key) || [];
    try {
      const parsed = JSON.parse(window.localStorage?.getItem(key) || 'null');
      if (parsed?.v === 1 && Array.isArray(parsed.units) && parsed.units.length <= 100 && parsed.units.every(value => typeof value === 'string')) return parsed.units;
    } catch { /* Storage can be disabled. Retain this page's in-memory order. */ }
    return memory.get(key) || [];
  };
  const fallbackUnits = items => {
    const units = [...new Set(items.map(unitOf))];
    const created = new Map(items.filter(bundleOf).map(item => [unitOf(item), manifestOf(item)?.created || Infinity]));
    const boards = units.filter(unit => unit.startsWith('board:')).sort((a, b) => created.get(a) - created.get(b));
    let i = 0;
    return units.map(unit => unit.startsWith('board:') ? boards[i++] : unit);
  };
  const prepare = (cart, { persist = true } = {}) => {
    const items = cart?.items || [];
    const scope = scopeOf(cart);
    if (scope) activeScope = scope;
    const key = scope || activeScope;
    const available = fallbackUnits(items);
    const previous = key ? readLedger(key) : [];
    const units = [...previous.filter(unit => available.includes(unit)), ...available.filter(unit => !previous.includes(unit))];
    // Optimistic and token-less Liquid snapshots must never delete ledger entries.
    if (key && scope && persist && !items.some(item => item.pending)) {
      memory.set(key, units);
      try {
        if (!window.localStorage?.setItem) throw new Error('Storage unavailable');
        window.localStorage.setItem(key, JSON.stringify({ v: 1, units }));
        ledgerMemoryOnly.delete(key);
      } catch { ledgerMemoryOnly.add(key); }
      latestCart = cart;
    }
    return units;
  };
  const getBoardOrder = cart => prepare(cart, { persist: false }).filter(unit => unit.startsWith('board:')).map(unit => unit.slice(6));
  const boardComponents = ['deck', 'grip', 'truck', 'wheel', 'bearing', 'hardware', 'skate_tool', 'assembly'];
  const orderBoardItems = items => items.map((item, index) => {
    const rank = boardComponents.indexOf(item.properties?._hp_board_bundle_component);
    return { item, index, rank: rank < 0 ? boardComponents.length : rank };
  }).sort((a, b) => a.rank - b.rank || a.index - b.index).map(entry => entry.item);
  const orderCart = cart => {
    if (!Array.isArray(cart?.items)) return cart;
    // Rendering receives optimistic removal/clear snapshots too. Only explicit
    // confirmed reads may change the saved placement of existing cart units.
    const units = prepare(cart, { persist: false });
    const groups = new Map();
    for (const item of cart.items) {
      const id = unitOf(item);
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    }
    return { ...cart, items: units.flatMap(id => {
      const items = groups.get(id) || [];
      return id.startsWith('board:') ? orderBoardItems(items) : items;
    }) };
  };
  const payloadOf = cart => ({ version: 1, mode: 'availability', cartToken: String(cart.token || ''), currency: String(cart.currency || ''), items: cart.items.map(normalizedItem), boardOrder: getBoardOrder(cart) });
  let activeLockToken = null;
  let localLockQueue = Promise.resolve();
  const withCartLock = async (task, signal, ownerToken) => {
    const aborted = () => Object.assign(new Error('Cart verification was cancelled.'), { name: 'AbortError' });
    if (signal?.aborted) throw aborted();
    if (ownerToken && ownerToken === activeLockToken) return task(ownerToken);
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 15000);
    const execute = async () => {
      clearTimeout(timer);
      if (signal?.aborted || controller.signal.aborted) throw aborted();
      const token = {};
      activeLockToken = token;
      try { return await task(token); } finally { if (activeLockToken === token) activeLockToken = null; }
    };
    try {
      if (window.navigator?.locks?.request) return await window.navigator.locks.request(`hp-cart-write:${window.location.hostname}`, { mode: 'exclusive', signal: controller.signal }, execute);
      const queued = localLockQueue.then(execute, execute);
      localLockQueue = queued.catch(() => {});
      return await queued;
    } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
  };
  const validateCart = cart => {
    const keys = new Set();
    if (!cart || !Array.isArray(cart.items) || typeof cart.token !== 'string' || !cart.token
      || typeof cart.currency !== 'string' || !/^[A-Z]{3}$/.test(cart.currency)
      || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0
      || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) throw new Error('Could not verify the updated cart. Please try again.');
    for (const item of cart.items) {
      if (!item || typeof item.key !== 'string' || !item.key || keys.has(item.key)
        || !/^[1-9]\d*$/.test(String(item.variant_id || item.id || ''))
        || !Number.isSafeInteger(item.quantity) || item.quantity < 1
        || !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0
        || !Object.hasOwn(item, 'properties')
        || (item.properties !== null && (typeof item.properties !== 'object' || Array.isArray(item.properties)))) throw new Error('Could not verify every cart item safely. Please try again.');
      keys.add(item.key);
    }
    if (cart.items.reduce((sum, item) => sum + item.quantity, 0) !== cart.item_count) throw new Error('Could not verify the cart quantities. Please try again.');
    return cart;
  };
  const requestCart = async (path = 'cart.js', options = {}, signal) => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 12000);
    try {
      const response = await window.fetch(`${window.Shopify?.routes?.root || '/'}${path}`, { credentials: 'same-origin', cache: 'no-store', ...options, headers: { Accept: 'application/json', ...options.headers }, signal: controller.signal });
      const cart = await response.json();
      if (!response.ok) throw new Error('Could not verify the updated cart. Please try again.');
      return validateCart(cart);
    } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
  };
  const staleError = () => Object.assign(new Error('Your cart changed during verification. Please try again.'), { code: 'AVAILABILITY_STALE' });
  const cleanupMemory = new Map();
  const cleanupMemoryOnly = new Set();
  const emittedRecoveries = new Set();
  let recoveryScope = null;
  let recoveryPresent = new Set();
  let cleanupPendingCount = 0;
  const isCleanupPending = () => cleanupPendingCount > 0;
  const cleanupKey = scope => scope?.replace('hp-cart-order:', 'hp-board-cleanup:');
  const readCleanup = scope => {
    if (!scope) return [];
    if (cleanupMemoryOnly.has(scope)) return cleanupMemory.get(scope) || [];
    try {
      const raw = window.localStorage?.getItem(cleanupKey(scope));
      if (raw && raw.length <= 65536) {
        const data = JSON.parse(raw);
        const reasons = ['VARIANT_NOT_FOUND', 'VARIANT_UNAVAILABLE', 'INSUFFICIENT_STOCK', 'MISSING_SELECTION', 'LEGACY_REBUILD_REQUIRED'];
        if (data?.v === 1 && Array.isArray(data.boards) && data.boards.length <= 50 && data.boards.every(board =>
          board && typeof board.bundleId === 'string' && /^[A-Za-z0-9_-]{8,80}$/.test(board.bundleId) && reasons.includes(board.reason)
          && ['pending', 'confirmed'].includes(board.phase)
          && Array.isArray(board.unavailableVariantIds) && board.unavailableVariantIds.length <= 8
          && board.unavailableVariantIds.every(id => typeof id === 'string' && /^[1-9]\d*$/.test(id))
          && Array.isArray(board.unavailableComponents) && board.unavailableComponents.length <= 8
          && board.unavailableComponents.every(component => ['deck','grip','wheel','bearing','truck','hardware','skate_tool','assembly'].includes(component))
          && (board.manifest === null || manifestOf({ properties: { _hp_board_manifest: JSON.stringify(board.manifest) } }))
        )) return data.boards;
      }
    } catch { /* BFCache can still recover from memory when storage is blocked. */ }
    return cleanupMemory.get(scope) || [];
  };
  const writeCleanup = (scope, boards) => {
    if (!scope) return;
    const bounded = boards.slice(-50);
    cleanupMemory.set(scope, bounded);
    try {
      if (!window.localStorage?.setItem) throw new Error('Storage unavailable');
      window.localStorage.setItem(cleanupKey(scope), JSON.stringify({ v: 1, boards: bounded }));
      cleanupMemoryOnly.delete(scope);
    } catch { cleanupMemoryOnly.add(scope); }
  };
  const rememberCleanup = (cart, boards) => {
    const scope = scopeOf(cart);
    const ids = new Set(boards.map(board => board.bundleId));
    ids.forEach(id => emittedRecoveries.delete(`${scope}:${id}`));
    const saved = boards.map(board => ({ bundleId: board.bundleId, reason: board.reason,
      unavailableVariantIds: [...board.unavailableVariantIds], unavailableComponents: [...board.unavailableComponents],
      manifest: manifestOf(cart.items.find(item => bundleOf(item) === board.bundleId)), phase: 'pending' }));
    writeCleanup(scope, [...readCleanup(scope).filter(board => !ids.has(board.bundleId)), ...saved]);
  };
  const replayRemovals = listener => {
    if (isCleanupPending()) return;
    const removed = readCleanup(recoveryScope).filter(board => board.phase === 'confirmed' && !recoveryPresent.has(board.bundleId));
    if (removed.length && typeof listener === 'function') listener({ detail: { removed } });
  };
  const acknowledgeRemovals = ids => {
    if (!recoveryScope) return;
    const consumed = new Set(ids);
    writeCleanup(recoveryScope, readCleanup(recoveryScope).filter(board => board.phase !== 'confirmed' || !consumed.has(board.bundleId)));
  };
  const retireHealthyCleanup = async (cart, result, { revision, signal } = {}) => {
    validateCart(cart);
    if (!Array.isArray(result?.removeBoards) || !Array.isArray(result?.blockingIssues) || result.blockingIssues.length
      || result.cartFingerprint !== await fingerprint(payloadOf(cart))) return;
    const mutations = window.HalfpipedCartMutations;
    if (signal?.aborted || mutations?.isPending?.() || (revision !== undefined && revision !== mutations?.getRevision?.())) return;
    const scope = scopeOf(cart);
    const present = new Set(cart.items.map(bundleOf));
    const unavailable = new Set(result.removeBoards.map(board => board.bundleId));
    const journal = readCleanup(scope);
    const retained = journal.filter(board => board.phase !== 'pending' || !present.has(board.bundleId) || unavailable.has(board.bundleId));
    // A fresh successful validation resolves an earlier failed cleanup. A later
    // manual deletion must not resurrect its historical unavailable selections.
    if (retained.length !== journal.length) writeCleanup(scope, retained);
  };
  const reconcileRemovals = (cart, { liveCleanup = false, cartRevision, cleanupBoardIds = [] } = {}) => {
    validateCart(cart);
    // Other cart readers may observe a successful write before its owner has
    // verified it. Keep both cart presentation and selection recovery together.
    if (isCleanupPending()) return;
    const mutations = window.HalfpipedCartMutations;
    if (Number.isSafeInteger(cartRevision)
      && (mutations?.isPending?.() || cartRevision !== mutations?.getRevision?.())) return;
    const scope = scopeOf(cart);
    if (!scope) return;
    recoveryScope = scope;
    const boards = readCleanup(scope);
    const present = new Set(cart.items.map(bundleOf));
    recoveryPresent = present;
    const absent = boards.filter(board => !present.has(board.bundleId));
    const removed = absent.filter(board => !emittedRecoveries.has(`${scope}:${board.bundleId}`));
    if (!removed.length && !liveCleanup) return;
    // The journal is a UI recovery receipt only. It can never authorize another
    // cart write; current availability must independently authorize each removal.
    const ids = new Set(removed.map(board => board.bundleId));
    writeCleanup(scope, boards.map(board => ids.has(board.bundleId) ? { ...board, phase: 'confirmed' } : board));
    ids.forEach(id => emittedRecoveries.add(`${scope}:${id}`));
    // Successful automatic cleanup is silent. Keep the receipt and recovery
    // event so unavailable selections are still cleared from the customiser.
    document.dispatchEvent(new CustomEvent('hp:boards:removed', { detail: { removed, cart,
      ...(liveCleanup ? { liveCleanup: true, cartRevision, cleanupBoardIds } : {}) } }));
    if (!liveCleanup && Number.isSafeInteger(cartRevision)
      && !mutations?.isPending?.() && cartRevision === mutations?.getRevision?.()) {
      // A lost verification response can leave a successful removal in the
      // journal. Its next verified read publishes recovery in the same task.
      const data = { source: 'board-integrity-recovery', cartConfirmed: true, cartRevision, itemCount: cart.item_count };
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { resource: cart, sourceId: 'board-integrity', data } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart, data } }));
    }
  };
  const removeBoards = async (cart, result, signal, { recoverFailureThroughRevision, availabilityStartedAt } = {}) => {
    const mutations = window.HalfpipedCartMutations;
    if (!mutations?.whenIdle || !mutations?.begin) throw new Error('Please reload the page to verify your board.');
    const ids = new Set(result.removeBoards.map(board => board.bundleId));
    let ownsPresentationHold = false;
    const releasePresentationHold = () => {
      if (!ownsPresentationHold) return;
      ownsPresentationHold = false;
      cleanupPendingCount = Math.max(0, cleanupPendingCount - 1);
    };
    for (const bundleId of ids) document.dispatchEvent(new CustomEvent('hp:board-removal:confirming', { detail: { bundleId, resume: [] } }));
    // Wait outside the lock: a writer already waiting for that lock may own a
    // mutation-registry token. Holding both would deadlock the two operations.
    try {
      await mutations.whenIdle({ signal, recoverFailureThroughRevision });
      const outcome = await withCartLock(async lockToken => {
        if (mutations.isPending()) throw staleError();
        const revision = mutations.getRevision();
        const fresh = await requestCart('cart.js', {}, signal);
        const allKeys = fresh.items.map(item => item.key);
        if (allKeys.some(key => typeof key !== 'string' || !key) || new Set(allKeys).size !== allKeys.length) throw new Error('Could not identify every cart item safely. Please try again.');
        const freshFingerprint = await fingerprint(payloadOf(fresh));
        if (signal?.aborted || mutations.isPending() || revision !== mutations.getRevision() || freshFingerprint !== result.cartFingerprint) throw staleError();
        const keys = fresh.items.filter(item => ids.has(bundleOf(item))).map(item => item.key);
        if (keys.some(key => typeof key !== 'string' || !key)) throw new Error('Could not identify every board item safely.');
        if (!keys.length) return { fresh, writeRevision: revision };
        rememberCleanup(fresh, result.removeBoards);
        cleanupPendingCount += 1;
        ownsPresentationHold = true;
        // These IDs come only from this fresh, fingerprint-checked global stock
        // result. Allocation shortages and historical receipts are not SKU truth.
        const unavailableVariantIds = [...new Set(result.removeBoards
          .filter(board => ['VARIANT_NOT_FOUND', 'VARIANT_UNAVAILABLE'].includes(board.reason))
          .flatMap(board => board.unavailableVariantIds))];
        document.dispatchEvent(new CustomEvent('hp:boards:cleanup-start', { detail: { unavailableVariantIds, cart: fresh,
          availabilityStartedAt: Number.isFinite(availabilityStartedAt) ? availabilityStartedAt : Date.now() } }));
        const finish = mutations.begin();
        let writeRevision;
        let writeError;
        try {
          const update = requestCart('cart/update.js', { method: 'POST', hpCartLockToken: lockToken, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: Object.fromEntries(keys.map(key => [key, 0])) }) }, signal);
          writeRevision = mutations.getRevision();
          try { await update; } catch (error) { writeError = error; }
        } finally { finish(); }
        return { fresh, writeRevision, writeError };
      }, signal);
      // A failed response may still have removed the board. Verify after releasing
      // the transaction lock so other queued cart mutations can finish too.
      let updated;
      let afterRevision;
      for (let attempt = 0; attempt < 3; attempt++) {
        await mutations.whenIdle({ signal, recoverFailureThroughRevision: outcome.writeRevision });
        afterRevision = mutations.getRevision();
        const candidate = await requestCart('cart.js', {}, signal);
        if (!mutations.isPending() && afterRevision === mutations.getRevision()) { updated = candidate; break; }
      }
      if (!updated) throw staleError();
      if (updated.items.some(item => ids.has(bundleOf(item)))) throw outcome.writeError || new Error('Could not remove every item in your unavailable board. Please try again.');
      prepare(updated);
      mutations.acknowledgeCart?.(afterRevision);
      releasePresentationHold();
      // Overlapping removals publish only when the last owner has verified the
      // current cart. An earlier snapshot cannot replace a still-pending write.
      if (!isCleanupPending()) {
        reconcileRemovals(updated, { liveCleanup: true, cartRevision: afterRevision, cleanupBoardIds: [...ids] });
        document.dispatchEvent(new CustomEvent('cart:update', { detail: { resource: updated, sourceId: 'board-integrity', data: { source: 'board-integrity', cartConfirmed: true, cartRevision: afterRevision, itemCount: updated.item_count } } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: updated } }));
      }
      return updated;
    } finally { releasePresentationHold(); }
  };
  const applyDOMOrder = () => {
    if (!latestCart || isCleanupPending()) return;
    document.querySelectorAll?.('.cart-items__table tbody').forEach(body => {
      const rows = [...body.querySelectorAll('[data-key]')];
      const ordered = orderCart(latestCart);
      const keys = ordered.items.map(item => String(item.key));
      const sorted = [...rows].sort((a, b) => keys.indexOf(a.dataset.key) - keys.indexOf(b.dataset.key));
      if (sorted.some((row, i) => row !== rows[i])) sorted.forEach(row => body.appendChild(row));
      // Section HTML starts in Shopify order; labels follow the visible ledger
      // order even when a later update only changes the number of bundles.
      const bundleIds = [...new Set(sorted.map(row => row.dataset.boardBundleId).filter(Boolean))];
      sorted.forEach(row => {
        const bundleId = row.dataset.boardBundleId;
        if (!bundleId) return;
        const number = bundleIds.indexOf(bundleId) + 1;
        const heading = `Custom complete bundle${bundleIds.length > 1 ? ` ${number}` : ''}`;
        const label = `Part of custom complete${bundleIds.length > 1 ? ` ${number}` : ''}`;
        for (const [selector, text] of [['[data-board-bundle-heading]', heading], ['[data-board-part-label]', label]]) {
          const node = row.querySelector?.(selector);
          // Avoid restarting the child-list observer for unchanged text.
          if (node && node.textContent !== text) node.textContent = text;
        }
      });
    });
  };
  const api = { sha256, fingerprint, manifestOf, lineIdentity, unitOf, prepare, orderCart, orderBoardItems, getBoardOrder, payloadOf, withCartLock, isCartLockOwner: token => Boolean(token && token === activeLockToken), validateCart, requestCart, removeBoards, reconcileRemovals, replayRemovals, acknowledgeRemovals, retireHealthyCleanup, isCleanupPending, applyDOMOrder };
  window.HalfpipedBoardIntegrity = api;
  if (document.addEventListener) {
    document.documentElement?.setAttribute('data-hp-cart-order-pending', '');
    const initialRevision = window.HalfpipedCartMutations?.getRevision?.();
    api.ready = requestCart().then(cart => {
      if (!window.HalfpipedCartMutations?.isPending?.() && initialRevision === window.HalfpipedCartMutations?.getRevision?.()) prepare(cart);
      return cart;
    }).catch(() => null);
    const start = async () => {
      const cart = await api.ready;
      if (cart && !window.HalfpipedCartMutations?.isPending?.() && initialRevision === window.HalfpipedCartMutations?.getRevision?.()) reconcileRemovals(cart, { cartRevision: initialRevision });
      applyDOMOrder();
      document.documentElement?.removeAttribute('data-hp-cart-order-pending');
      if (typeof MutationObserver !== 'undefined' && document.body) new MutationObserver(() => applyDOMOrder()).observe(document.body, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else void start();
    ['cart:update', 'hp:cart:priced'].forEach(type => document.addEventListener(type, event => {
      const cart = event.detail?.resource || event.detail?.cart;
      const data = event.detail?.data || event.detail;
      if (cart?.token && Array.isArray(cart.items) && (type === 'hp:cart:priced' || data?.cartConfirmed === true)
        && data?.cartRevision === window.HalfpipedCartMutations?.getRevision?.() && !window.HalfpipedCartMutations?.isPending?.()) { prepare(cart); applyDOMOrder(); }
    }));
  }
})();
